import { useEffect, useId, useRef, type ReactNode } from 'react';

const WIDTH = {
  /** Questions and short forms. */
  sm: 'max-w-sm',
  /** Forms with several sections: settings, a specification, an indicator. */
  md: 'max-w-lg',
  /** Tables and wide content. */
  lg: 'max-w-3xl',
  /** A form next to a side panel, such as a live chart. */
  xl: 'max-w-5xl',
} as const;

/**
 * A modal dialog on the native <dialog> element: focus is trapped, Escape closes it, it has a proper title and a close (×)
 * button, and a width that suits what it holds.
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  size = 'sm',
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  size?: keyof typeof WIDTH;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={onClose}
      className={`m-auto max-h-[90vh] w-[calc(100%-2rem)] ${WIDTH[size]} overflow-auto rounded-[var(--radius)] border border-line bg-panel p-0 text-fg shadow-2xl backdrop:bg-black/60`}
    >
      {open && (
        <div className="p-4">
          <div className="mb-3 flex items-start gap-3">
            <h2 id={titleId} className="flex-1 text-base font-semibold">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              title="Close (Esc)"
              className="-mr-1 -mt-1 rounded px-2 py-0.5 text-lg leading-none text-muted hover:bg-panel2 hover:text-fg"
            >
              ×
            </button>
          </div>
          {children}
        </div>
      )}
    </dialog>
  );
}
